import { ReactNode, createContext, useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { match } from "ts-pattern";

export enum ETab {
	FORUM = "FORUM",
	VOTE = "VOTE",
	CASTING = "CASTING",
}

export type ITabContext = {
	activeTab: ETab;
	setActiveTab: (tab: ETab) => void;
	isActiveTab: (tab: ETab) => boolean;
	isTabEnabled: (tab: ETab) => boolean;
};

type IProps = {
	enabledTabs?: ETab[];
	defaultTab?: ETab;
	children: ReactNode;
};

export const TabContext = createContext<ITabContext>(undefined!);

export function TabProvider(props: IProps) {
	const { activeTab, setActiveTab, isTabEnabled } = useTabSearchParams(props);

	return (
		<TabContext.Provider
			value={{
				activeTab,
				setActiveTab,
				isActiveTab: (tab: ETab) => activeTab === tab,
				isTabEnabled,
			}}>
			{props.children}
		</TabContext.Provider>
	);
}

const useTabSearchParams = (props: Omit<IProps, "children">) => {
	const [searchParams, setSearchParams] = useSearchParams();

	const defaultTab = props.defaultTab ?? ETab.FORUM;

	const isTabEnabled = useCallback(
		(tab: ETab) => {
			return !props.enabledTabs || props.enabledTabs.includes(tab);
		},
		[props.enabledTabs],
	);

	const getTabFromUrl = useCallback((): ETab => {
		const tab = searchParams.get("castingTab");

		return match(tab?.toUpperCase())
			.with("FORUM", () => (isTabEnabled(ETab.FORUM) ? ETab.FORUM : defaultTab))
			.with("VOTE", () => (isTabEnabled(ETab.VOTE) ? ETab.VOTE: defaultTab))
			.with("CASTING", () => (isTabEnabled(ETab.CASTING) ? ETab.CASTING : defaultTab))
			.otherwise(() => defaultTab);
	}, [defaultTab, isTabEnabled, searchParams]);

	const [activeTab, setActiveTab] = useState<ETab>(getTabFromUrl);

	const setTabInUrl = useCallback(
		(tab: ETab) => {
			searchParams.set("castingTab", ETab[tab].toLowerCase());
			setSearchParams(searchParams);
			setActiveTab(tab);
		},
		[searchParams, setSearchParams],
	);

	useEffect(() => {
		const tab = getTabFromUrl();

		if (tab !== activeTab) {
			setActiveTab(tab);
		}
	}, [activeTab, getTabFromUrl]);

	return {
		activeTab,
		setActiveTab: setTabInUrl,
		isTabEnabled,
	};
};
